import axios, { AxiosError } from 'axios';

interface ApiErrorResponse {
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
}

/**
 * Extrai uma mensagem de erro legível de qualquer tipo de erro
 */
export function getErrorMessage(error: unknown): string {
  // Erro do Axios
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiErrorResponse>;

    // Erro do backend com mensagem
    if (axiosError.response?.data) {
      const data = axiosError.response.data;

      // Mensagem direta
      if (data.message) {
        return data.message;
      }

      // Campo error
      if (data.error) {
        return data.error;
      }

      // Erros de validação
      if (data.errors) {
        const firstError = Object.values(data.errors)[0];
        if (Array.isArray(firstError) && firstError.length > 0) {
          return firstError[0];
        }
      }
    }

    // Erro de rede (servidor offline, CORS, etc)
    if (axiosError.code === 'ERR_NETWORK') {
      return 'Erro de conexão com o servidor. Verifique sua internet.';
    }

    // Timeout
    if (axiosError.code === 'ECONNABORTED') {
      return 'Servidor iniciando, aguarde alguns segundos e tente novamente.';
    }

    // Erro HTTP sem mensagem customizada
    if (axiosError.response?.status) {
      const status = axiosError.response.status;
      switch (status) {
        case 400:
          return 'Requisição inválida. Verifique os dados enviados.';
        case 401:
          return 'Não autorizado. Faça login novamente.';
        case 403:
          return 'Acesso negado. Você não tem permissão.';
        case 404:
          return 'Recurso não encontrado.';
        case 409:
          return 'Conflito. Este registro já existe.';
        case 422:
          return 'Dados inválidos. Verifique os campos.';
        case 500:
          return 'Erro interno do servidor. Tente novamente mais tarde.';
        case 502:
        case 503:
        case 504:
          return 'Servidor indisponível. Tente novamente mais tarde.';
        default:
          return `Erro do servidor (${status})`;
      }
    }

    // Fallback para mensagem do Axios
    return axiosError.message || 'Erro de conexão';
  }

  // Erro padrão do JavaScript
  if (error instanceof Error) {
    return error.message;
  }

  // String direta
  if (typeof error === 'string') {
    return error;
  }

  return 'Ocorreu um erro inesperado';
}

/**
 * Loga o erro completo no console para debug
 */
export function logError(error: unknown, context?: string): void {
  const prefix = context ? `[${context}]` : '[Error]';

  if (axios.isAxiosError(error)) {
    console.error(prefix, {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method,
      data: error.response?.data,
      code: error.code,
    });
  } else {
    console.error(prefix, error);
  }
}
